import { MAIN_ROUTES, MainRoutesProps } from 'routes';
import './LeftNavigationBar.css';
import { useNavigate, Link } from 'react-router';
import MagicButton from 'components/MagicButton/MagicButton';
import { useIsAdmin } from 'hooks/useIsAdmin';
import { useState, useEffect, useRef, forwardRef } from 'react';
import FeedbackModal from 'components/FeedbackModal/FeedbackModal';
import { useSubmitFeedbackMutation } from 'store/services/feedback/feedbackApi';
import { toast } from 'react-toastify';
import { AssetUrls } from 'config/assets';
import { useGetUnreadCountQuery } from 'store/services/message/messageApi';
import { useGetInboundTradeCountQuery } from 'store/services/trade/tradeApi';

interface Props {
  currentTab: MainRoutesProps;
  isMobile?: boolean;
  isMobileMenuOpen?: boolean;
  onMenuClose?: () => void;
  isPublicMode?: boolean;
  onNavigationAttempt?: () => void;
  isAuthenticated?: boolean;
}

const getNavigationBarItems = (
  currentTab: MainRoutesProps,
  isMobile: boolean,
  onMenuClose?: () => void,
  isPublicMode?: boolean,
  onNavigationAttempt?: () => void,
  isAuthenticated?: boolean,
  unreadCount?: number,
  inboundTradeCount?: number,
  hasOverdueTrades?: boolean
) => {
  return MAIN_ROUTES.filter((route) => route.path !== '/search').map(
    (route) => {
      const isSelected = currentTab.path === route.path;
      const icon = isSelected ? route.icons?.selected : route.icons?.default;
      const labelClassName = `left-navigation-bar-item-label ${
        route.path === currentTab.path ? 'selected' : ''
      }`;
      const itemClassName = `left-navigation-bar-item ${
        route.path === currentTab.path ? 'selected' : ''
      }`;

      const isInbox = route.path === '/inbox';
      const showInboxBadge = isInbox && unreadCount !== undefined && unreadCount > 0;

      const isReceiving = route.path === '/receiving';
      const showTradeBadge = isReceiving && inboundTradeCount !== undefined && inboundTradeCount > 0;
      const tradeBadgeClassName = `inbound-trade-badge ${hasOverdueTrades ? 'overdue' : 'normal'}`;

      return (
        <Link
          key={`left-navigation-bar-item-${route.title}`}
          to={route.path}
          className={itemClassName}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              e.preventDefault();
              onNavigationAttempt();
              return;
            }
            if (isMobile && onMenuClose) {
              onMenuClose();
            }
          }}
        >
          <img src={icon} alt="navigation-icon" />
          <div className={labelClassName}>{route.title}</div>
          {showInboxBadge && unreadCount !== undefined && (
            <span className="unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
          )}
          {showTradeBadge && inboundTradeCount !== undefined && (
            <span className={tradeBadgeClassName}>{inboundTradeCount > 99 ? '99+' : inboundTradeCount}</span>
          )}
        </Link>
      );
    }
  );
};

export const LeftNavigationBar = forwardRef<HTMLDivElement, Props>(({ currentTab, isMobile = false, isMobileMenuOpen = false, onMenuClose, isPublicMode = false, onNavigationAttempt, isAuthenticated = false }, ref) => {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin(isPublicMode); // Skip API call in public mode
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [submitFeedback, { isLoading: isSubmittingFeedback }] = useSubmitFeedbackMutation();

  // Activity-based polling state
  const [pollingInterval, setPollingInterval] = useState(10000); // Default 10s
  const lastActivityRef = useRef(Date.now());
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch unread count with dynamic polling (skip in public mode)
  const { data: unreadData } = useGetUnreadCountQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
    skip: isPublicMode,
  });

  // Fetch inbound trade count with dynamic polling (skip in public mode)
  const { data: inboundTradeData } = useGetInboundTradeCountQuery(undefined, {
    pollingInterval,
    refetchOnMountOrArgChange: true,
    skip: isPublicMode,
  });

  // Track user activity
  useEffect(() => {
    const trackActivity = () => {
      lastActivityRef.current = Date.now();

      // Reset polling to 10s on activity
      setPollingInterval(10000);

      // Clear existing inactivity timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      // Set new inactivity timer (5 minutes)
      inactivityTimerRef.current = setTimeout(() => {
        setPollingInterval(0); // Stop polling after 5min inactivity
      }, 5 * 60 * 1000);
    };

    // Listen to user activity events
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => window.addEventListener(event, trackActivity));

    // Initial activity tracking
    trackActivity();

    return () => {
      events.forEach(event => window.removeEventListener(event, trackActivity));
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, []);

  const handleFeedbackSubmit = async (feedbackData: any) => {
    try {
      const result = await submitFeedback(feedbackData).unwrap();
      if (result.success) {
        toast.success('Thank you for your feedback!');
        setIsFeedbackModalOpen(false);
      } else {
        toast.error(result.message || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  const isSearchSelected = currentTab.path === '/search';
  const searchClassName = `left-navigation-bar-item-label ${
    isSearchSelected ? 'selected' : ''
  }`;
  const searchItemClassName = `left-navigation-bar-item ${
    isSearchSelected ? 'selected' : ''
  }`;

  const unreadCount = unreadData?.count || 0;
  const inboundTradeCount = inboundTradeData?.count || 0;
  const hasOverdueTrades = inboundTradeData?.hasOverdueTrades || false;
  const navigationBarItems = getNavigationBarItems(
    currentTab,
    isMobile,
    onMenuClose,
    isPublicMode,
    onNavigationAttempt,
    isAuthenticated,
    unreadCount,
    inboundTradeCount,
    hasOverdueTrades
  );

  return (
    <div ref={ref} className={`left-navigation-bar ${isMobile ? 'mobile' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}>
      {/* Logo Header */}
      <div className="left-navigation-bar-header">
        <img
          src={AssetUrls.logos.iconLogo}
          width={30}
          alt="icon-logo"
        />
        <div className="left-navigation-bar-header-title">trademagic.gg</div>
      </div>

      {/* Send Cards Button - Wrapped in div to handle stopPropagation */}
      <div onClick={(event) => event.stopPropagation()}>
        <MagicButton
          label="Send Cards"
          icon={
            <img
              src={AssetUrls.logos.arevakhachIcon}
              alt="send-icon"
              style={{ width: 20, height: 20 }}
            />
          }
          color="purple"
          fullWidth
          onPress={() => {
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              onNavigationAttempt();
            } else {
              navigate('/sendcards');
              if (isMobile && onMenuClose) {
                onMenuClose();
              }
            }
          }}
        />
      </div>

      {/* Navigation Items */}
      <div className="left-navigation-bar-items-container">
        {/* Search Item */}
        <Link
          to="/search"
          className={searchItemClassName}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              e.preventDefault();
              onNavigationAttempt();
              return;
            }
            if (isMobile && onMenuClose) {
              onMenuClose();
            }
          }}
        >
          <img
            src={AssetUrls.icons.navigation.search}
            alt="search-icon"
          />
          <div className={searchClassName}>Search</div>
        </Link>

        {/* Existing routes from MAIN_ROUTES */}
        {navigationBarItems}

        {/* Feedback Link */}
        <div
          className="left-navigation-bar-item"
          onClick={(event) => {
            event.stopPropagation();
            // In public mode, only prevent navigation if user is not authenticated
            if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
              onNavigationAttempt();
            } else {
              setIsFeedbackModalOpen(true);
            }
          }}
        >
          <img
            src={AssetUrls.icons.navigation.adminFeedback}
            alt="feedback-icon"
          />
          <div className="left-navigation-bar-item-label">Feedback</div>
        </div>

        {/* Admin Link - Only visible to admins */}
        {isAdmin && (
          <Link
            to="/admin/feedback"
            className="left-navigation-bar-item"
            onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
              // In public mode, only prevent navigation if user is not authenticated
              if (isPublicMode && !isAuthenticated && onNavigationAttempt) {
                e.preventDefault();
                onNavigationAttempt();
                return;
              }
              if (isMobile && onMenuClose) {
                onMenuClose();
              }
            }}
          >
            <img
              src={AssetUrls.icons.navigation.admin}
              alt="admin-icon"
            />
            <div className="left-navigation-bar-item-label">Admin</div>
          </Link>
        )}
      </div>
      
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
        onSubmit={handleFeedbackSubmit}
        isLoading={isSubmittingFeedback}
      />
    </div>
  );
});

LeftNavigationBar.displayName = 'LeftNavigationBar';
